import { useEffect, useState } from 'react';
import { useClerk } from '@clerk/clerk-react';
import { useNavigate, Link } from 'react-router-dom';
import MarketingLayout from '../components/MarketingLayout';

/**
 * Handles the email link callback from Clerk after health-check signup.
 * When the user clicks the link in their email, they land here; we verify and redirect to dashboard.
 */
export default function HealthCheckVerifyPage() {
  const { handleEmailLinkVerification, loaded } = useClerk();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'verified' | 'expired' | 'failed'>('loading');

  useEffect(() => {
    if (!loaded || !handleEmailLinkVerification) return;

    const run = async () => {
      try {
        const protocol = window.location.protocol;
        const host = window.location.host;
        await handleEmailLinkVerification({
          redirectUrl: `${protocol}//${host}/health-check/verify`,
        });
        setStatus('verified');
        navigate('/dashboard', { replace: true });
      } catch (err: any) {
        const code = err?.code ?? '';
        if (code === 'expired' || err?.errors?.[0]?.code === 'verification_expired') {
          setStatus('expired');
        } else {
          setStatus('failed');
        }
      }
    };

    run();
  }, [loaded, handleEmailLinkVerification, navigate]);

  const title =
    status === 'expired'
      ? 'A fresh link will get you moving.'
      : status === 'failed'
        ? 'Let’s reconnect you to your plan.'
        : 'Connecting your results to your workspace.';
  const description =
    status === 'expired'
      ? 'Your email link has expired. Return to the health check to request a new one.'
      : status === 'failed'
        ? 'We couldn’t verify this email link. Sign in with your email, or request another link from the health check.'
        : 'We’re verifying your email and opening your dashboard so you can take the next step.';

  return (
    <MarketingLayout>
      <section className="public-section">
        <div className="public-container">
          <div className="public-card public-card--white p-8 max-w-xl mx-auto text-center">
            {(status === 'loading' || status === 'verified') && (
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-6" />
            )}
            <p className="public-eyebrow">From insight to your next move</p>
            <h1 className="text-3xl font-bold mb-4">{title}</h1>
            <p
              className="text-gray-600 mb-7"
              role={status === 'failed' || status === 'expired' ? 'alert' : 'status'}
            >
              {description}
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              {status === 'failed' && (
                <Link to="/sign-in" className="public-button public-button--primary">
                  Sign in with your email
                </Link>
              )}
              {(status === 'expired' || status === 'failed') && (
                <Link
                  to="/health-check"
                  className={`public-button ${status === 'expired' ? 'public-button--primary' : 'public-button--secondary'}`}
                >
                  Return to the health check
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
